import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ScarcityIcon from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { range } from '@sorare/core/src/lib/arrays';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';

import { CompetitionDetailsGameplayCardRequirements_LeaderboardRequirementsInterface } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 1 })``;

const InlineWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--half-unit);
  &.showCards {
    gap: var(--intermediate-unit);
  }
`;

const RarityWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const CardWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--half-unit);
  padding-left: var(--half-unit);
  & > * + * {
    /**
     * Using adjacency selector ('+') to shift all cards after the first to the
     * left to create an overlapping group of cards.
     */
    margin-left: calc(-1 * var(--unit));
  }
  & > * {
    box-shadow: -1px 0px 1px rgb(255 255 255 / 50%);
    &.common {
      border: 1px solid rgb(0 0 0 / 5%);
    }
  }
`;

const Plus = styled.span`
  padding-left: 1px;
`;

type Props = {
  requirements?: CompetitionDetailsGameplayCardRequirements_LeaderboardRequirementsInterface | null;
  nbCards: number;
  showCards?: boolean;
};

export const CardRequirements = ({
  requirements,
  nbCards = 5,
  showCards: showCardsProp = false,
}: Props) => {
  if (!requirements) {
    return <Skeleton loading />;
  }
  const { allowedRarities } = requirements;
  if (allowedRarities.length === 0) {
    return null;
  }
  const hasMixedCards = allowedRarities.length > 2;
  const showCards = hasMixedCards ? false : showCardsProp;
  const rarities = sortByRarity(allowedRarities);
  if (!showCards) {
    return (
      <Wrapper>
        <InlineWrapper>
          {rarities
            .reverse()
            .map(rarity => formatScarcity(rarity))
            .join(', ')}
        </InlineWrapper>
        {requirements.minCurrentSeasonCardCount > 0 && (
          <LabelM as="div" color="var(--c-red-800)">
            <FormattedMessage
              id="CardRequirements.minCurrentSeasonCardCount"
              defaultMessage="Requires {minCurrentSeasonCardCount} cards from current season"
              values={{
                minCurrentSeasonCardCount:
                  requirements.minCurrentSeasonCardCount,
              }}
            />
          </LabelM>
        )}
      </Wrapper>
    );
  }
  const minCount = requirements.minRarity?.minCount ?? 0;
  return (
    <Wrapper>
      <InlineWrapper className={classNames({ showCards })}>
        {rarities.map(rarity => {
          const name = formatScarcity(rarity);
          const isMinRarity = rarity === requirements.minRarity?.rarity;
          const nb = isMinRarity ? minCount : nbCards - minCount;
          return (
            <RarityWrapper key={rarity}>
              {name}
              {nb > 0 ? (
                <CardWrapper>
                  {range(nb).map(i => (
                    <ScarcityIcon key={i} scarcity={rarity} />
                  ))}
                </CardWrapper>
              ) : null}
              {isMinRarity && nb !== nbCards && <Plus>+</Plus>}
            </RarityWrapper>
          );
        })}
      </InlineWrapper>
    </Wrapper>
  );
};

CardRequirements.fragments = {
  LeaderboardRequirementsInterface: gql`
    fragment CompetitionDetailsGameplayCardRequirements_LeaderboardRequirementsInterface on LeaderboardRequirementsInterface {
      allowedRarities
      minRarity {
        minCount
        rarity
      }
      minCurrentSeasonCardCount
    }
  ` as TypedDocumentNode<CompetitionDetailsGameplayCardRequirements_LeaderboardRequirementsInterface>,
};
